import React, { useState, useEffect } from 'react';
import { getDatabase, ref, onValue } from '../../Config/matuRtdb';
import { watchPredictionSnapshot } from '../../Config/matudb';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  DollarSign, 
  Calendar,
  Download,
  Filter,
  RefreshCw,
  Activity,
  Target,
  Crown,
  User
} from 'lucide-react';

const Analytics = () => {
  const [data, setData] = useState({
    users: [],
    predictions: [],
    revenue: []
  });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');
  const [selectedMetric, setSelectedMetric] = useState('users');

  useEffect(() => {
    loadAnalyticsData();
  }, [timeRange]);

  const loadAnalyticsData = () => {
    setLoading(true);
    const db = getDatabase();
    
    // Cargar datos de usuarios
    const usersRef = ref(db, 'usuarios');
    
    const unsubscribeUsers = onValue(usersRef, (snapshot) => {
      if (snapshot.exists()) {
        const usersData = snapshot.val();
        const usersArray = Object.keys(usersData).map(key => ({
          uid: key,
          ...usersData[key]
        }));
        
        setData(prev => ({ ...prev, users: usersArray }));
      }
    });

    const unsubscribePredictions = watchPredictionSnapshot('abet', (snapshot) => {
      if (snapshot.exists()) {
        const predictionsData = snapshot.val();
        const predictionsArray = Object.keys(predictionsData).map(key => ({
          id: key,
          ...predictionsData[key]
        }));
        
        setData(prev => ({ ...prev, predictions: predictionsArray }));
      }
      setLoading(false);
    });

    return () => {
      unsubscribeUsers();
      unsubscribePredictions();
    };
  };

  const getTimeRangeData = (data, range) => {
    const now = new Date();
    const daysAgo = {
      '7d': 7,
      '30d': 30,
      '90d': 90,
      '1y': 365
    }[range] || 30;

    const cutoffDate = new Date(now.getTime() - (daysAgo * 24 * 60 * 60 * 1000));
    
    return data.filter(item => {
      if (!item.createdAt) return false;
      const itemDate = new Date(item.createdAt);
      return itemDate >= cutoffDate;
    });
  };

  const generateChartData = () => {
    const filteredUsers = getTimeRangeData(data.users, timeRange);
    const filteredPredictions = getTimeRangeData(data.predictions, timeRange);

    // Datos para gráfico de usuarios por día
    const userChartData = generateDailyData(filteredUsers, 'createdAt', 'Usuarios Registrados');
    
    // Datos para gráfico de pronósticos por día
    const predictionChartData = generateDailyData(filteredPredictions, 'createdAt', 'Pronósticos Publicados');
    
    // Datos para gráfico de éxito de pronósticos
    const successChartData = generateSuccessData(filteredPredictions);

    return {
      users: userChartData,
      predictions: predictionChartData,
      success: successChartData
    };
  };

  const generateDailyData = (data, dateField, label) => {
    const days = {};
    const now = new Date();
    const daysAgo = {
      '7d': 7,
      '30d': 30,
      '90d': 90,
      '1y': 365
    }[timeRange] || 30;

    // Inicializar todos los días con 0
    for (let i = daysAgo - 1; i >= 0; i--) {
      const date = new Date(now.getTime() - (i * 24 * 60 * 60 * 1000));
      const dateStr = date.toISOString().split('T')[0];
      days[dateStr] = 0;
    }

    // Contar elementos por día
    data.forEach(item => {
      if (item[dateField]) {
        const dateStr = new Date(item[dateField]).toISOString().split('T')[0];
        if (days[dateStr] !== undefined) {
          days[dateStr]++;
        }
      }
    });

    return {
      labels: Object.keys(days),
      datasets: [{
        label,
        data: Object.values(days),
        borderColor: '#3B82F6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4
      }]
    };
  };

  const generateSuccessData = (predictions) => {
    const completed = predictions.filter(p => p.state === 'ganado' || p.state === 'perdido');
    const won = predictions.filter(p => p.state === 'ganado');
    const lost = predictions.filter(p => p.state === 'perdido');
    const pending = predictions.filter(p => p.state === 'pendiente');

    return {
      labels: ['Ganados', 'Perdidos', 'Pendientes'],
      datasets: [{
        data: [won.length, lost.length, pending.length],
        backgroundColor: ['#10B981', '#EF4444', '#F59E0B'],
        borderWidth: 0
      }]
    };
  };

  const getMetrics = () => {
    const filteredUsers = getTimeRangeData(data.users, timeRange);
    const filteredPredictions = getTimeRangeData(data.predictions, timeRange);

    const totalUsers = filteredUsers.length;
    const vipUsers = filteredUsers.filter(u => u.typeU === 'Cuenta Vip').length;
    const freeUsers = filteredUsers.filter(u => u.typeU === 'Cuenta Gratis').length;
    
    const totalPredictions = filteredPredictions.length;
    const completedPredictions = filteredPredictions.filter(p => p.state === 'ganado' || p.state === 'perdido');
    const successRate = completedPredictions.length > 0 
      ? (filteredPredictions.filter(p => p.state === 'ganado').length / completedPredictions.length) * 100 
      : 0;

    const avgConfidence = filteredPredictions.length > 0
      ? filteredPredictions.reduce((sum, p) => sum + (p.confidence || 0), 0) / filteredPredictions.length
      : 0;

    return {
      totalUsers,
      vipUsers,
      freeUsers,
      totalPredictions,
      successRate: Math.round(successRate * 10) / 10,
      avgConfidence: Math.round(avgConfidence * 10) / 10
    };
  };

  const metrics = getMetrics();
  const chartData = generateChartData();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white">Analytics Avanzados</h2>
        <p className="text-white/60">Métricas detalladas y análisis de rendimiento</p>
      </div>

      {/* Controles */}
      <div className="bg-white/10 backdrop-blur-lg rounded-lg p-4 border border-white/20">
        <div className="flex flex-col md:flex-row gap-4">
          <div>
            <label className="block text-sm font-medium text-white/60 mb-2">Rango de Tiempo</label>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="7d">Últimos 7 días</option>
              <option value="30d">Últimos 30 días</option>
              <option value="90d">Últimos 90 días</option>
              <option value="1y">Último año</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-white/60 mb-2">Métrica</label>
            <select
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
              className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="users">Usuarios</option>
              <option value="predictions">Pronósticos</option>
              <option value="success">Tasa de Éxito</option>
            </select>
          </div>

          <div className="flex items-end space-x-2">
            <button
              onClick={loadAnalyticsData}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center space-x-2 transition-colors"
            >
              <RefreshCw size={16} />
              <span>Actualizar</span>
            </button>
            
            <button className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center space-x-2 transition-colors">
              <Download size={16} />
              <span>Exportar</span>
            </button>
          </div>
        </div>
      </div>

      {/* Métricas Principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-4 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Total Usuarios</p>
              <p className="text-2xl font-bold text-white">{metrics.totalUsers}</p>
            </div>
            <Users className="text-blue-400" size={24} />
          </div>
          <div className="mt-2 flex items-center space-x-2 text-sm">
            <span className="text-white/60">VIP:</span>
            <span className="text-yellow-400">{metrics.vipUsers}</span>
            <span className="text-white/60">|</span>
            <span className="text-white/60">Gratis:</span>
            <span className="text-green-400">{metrics.freeUsers}</span>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-4 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Pronósticos</p>
              <p className="text-2xl font-bold text-white">{metrics.totalPredictions}</p>
            </div>
            <Target className="text-green-400" size={24} />
          </div>
          <div className="mt-2 flex items-center space-x-2 text-sm">
            <span className="text-white/60">Tasa de éxito:</span>
            <span className="text-green-400">{metrics.successRate}%</span>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-4 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Confianza Promedio</p>
              <p className="text-2xl font-bold text-yellow-400">{metrics.avgConfidence}%</p>
            </div>
            <Activity className="text-yellow-400" size={24} />
          </div>
          <div className="mt-2 flex items-center space-x-2 text-sm">
            <span className="text-white/60">Basado en</span>
            <span className="text-white">{metrics.totalPredictions} pronósticos</span>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico de Línea */}
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 border border-white/20">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
            <TrendingUp className="text-blue-400" size={20} />
            <span>
              {selectedMetric === 'users' ? 'Registro de Usuarios' :
               selectedMetric === 'predictions' ? 'Pronósticos Publicados' :
               'Tasa de Éxito'}
            </span>
          </h3>
          
          <div className="h-64 flex items-center justify-center">
            <div className="text-center">
              <BarChart3 className="text-white/40 mx-auto mb-2" size={48} />
              <p className="text-white/60 text-sm">Gráfico de {timeRange}</p>
              <p className="text-white/40 text-xs">
                {selectedMetric === 'users' ? `${metrics.totalUsers} usuarios registrados` :
                 selectedMetric === 'predictions' ? `${metrics.totalPredictions} pronósticos publicados` :
                 `${metrics.successRate}% tasa de éxito`}
              </p>
            </div>
          </div>
        </div>

        {/* Gráfico de Dona */}
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 border border-white/20">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
            <BarChart3 className="text-green-400" size={20} />
            <span>Distribución de Usuarios</span>
          </h3>
          
          <div className="h-64 flex items-center justify-center">
            <div className="text-center">
              <div className="w-32 h-32 rounded-full border-8 border-blue-400 mx-auto mb-4 relative">
                <div className="absolute inset-0 rounded-full border-8 border-yellow-400" 
                     style={{
                       clipPath: `polygon(50% 50%, 50% 0%, ${50 + (metrics.vipUsers / metrics.totalUsers) * 100}% 0%, ${50 + (metrics.vipUsers / metrics.totalUsers) * 100}% 50%)`
                     }}>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-white/60">VIP:</span>
                  <span className="text-yellow-400">{metrics.vipUsers}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-white/60">Gratis:</span>
                  <span className="text-green-400">{metrics.freeUsers}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Análisis Detallado */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tendencias */}
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 border border-white/20">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
            <TrendingUp className="text-purple-400" size={20} />
            <span>Tendencias</span>
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
              <div>
                <p className="text-white font-medium">Crecimiento de Usuarios</p>
                <p className="text-white/60 text-sm">Últimos {timeRange}</p>
              </div>
              <div className="text-right">
                <p className="text-green-400 font-bold">+{Math.round(metrics.totalUsers * 0.15)}</p>
                <p className="text-white/60 text-xs">+15%</p>
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
              <div>
                <p className="text-white font-medium">Conversión VIP</p>
                <p className="text-white/60 text-sm">Tasa de conversión</p>
              </div>
              <div className="text-right">
                <p className="text-yellow-400 font-bold">
                  {metrics.totalUsers > 0 ? Math.round((metrics.vipUsers / metrics.totalUsers) * 100) : 0}%
                </p>
                <p className="text-white/60 text-xs">{metrics.vipUsers} usuarios</p>
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
              <div>
                <p className="text-white font-medium">Engagement</p>
                <p className="text-white/60 text-sm">Pronósticos por usuario</p>
              </div>
              <div className="text-right">
                <p className="text-blue-400 font-bold">
                  {metrics.totalUsers > 0 ? Math.round(metrics.totalPredictions / metrics.totalUsers) : 0}
                </p>
                <p className="text-white/60 text-xs">promedio</p>
              </div>
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 border border-white/20">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
            <Target className="text-red-400" size={20} />
            <span>KPIs Clave</span>
          </h3>
          
          <div className="space-y-4">
            <div className="p-3 bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-500/20 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-white font-medium">Tasa de Éxito</span>
                <span className="text-green-400 font-bold">{metrics.successRate}%</span>
              </div>
              <div className="mt-2 w-full bg-white/20 rounded-full h-2">
                <div 
                  className="bg-green-400 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(metrics.successRate, 100)}%` }}
                ></div>
              </div>
            </div>
            
            <div className="p-3 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/20 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-white font-medium">Confianza Promedio</span>
                <span className="text-yellow-400 font-bold">{metrics.avgConfidence}%</span>
              </div>
              <div className="mt-2 w-full bg-white/20 rounded-full h-2">
                <div 
                  className="bg-yellow-400 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(metrics.avgConfidence, 100)}%` }}
                ></div>
              </div>
            </div>
            
            <div className="p-3 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-white font-medium">Retención VIP</span>
                <span className="text-purple-400 font-bold">
                  {metrics.totalUsers > 0 ? Math.round((metrics.vipUsers / metrics.totalUsers) * 100) : 0}%
                </span>
              </div>
              <div className="mt-2 w-full bg-white/20 rounded-full h-2">
                <div 
                  className="bg-purple-400 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min((metrics.vipUsers / metrics.totalUsers) * 100, 100)}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics; 